import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Briefcase, Award, Globe, Github, Phone } from "lucide-react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "../styles/experiences.css";

const expData = [
  {
    title: "Network Success Sr Manager - East - Big Star Transit",
    period: "06/2026 - Present",
    start: 2026,
    summary:
      "Lead provider network success for an asset-light paratransit and NEMT transportation network across assigned eastern divisions.",
    details: [
      "Oversee Divisions 6, 7, 8, and 11 with responsibility for provider network productivity, service reliability, and contract discipline.",
      "Support and guide Network Success Managers who manage productive, profitable, and customer-centric provider relationships.",
      "Coordinate with Quality Control, Fleet Administration, Safety, Deployment, and Client Success teams to keep network capacity aligned with contractual service obligations.",
      "Use performance oversight, provider reviews, policy consistency, and pre-emptive replacement planning to support network reliability.",
      "Maintain focus on safety, local/state/federal compliance, stakeholder coordination, and high-performance virtual team leadership.",
    ],
  },
  {
    title: "Founder & Owner - Old Alex Hub",
    period: "2022 - Present",
    start: 2022,
    summary:
      "Lead a venture-building and investment company across mobility, SaaS, mobile products, AI, real estate, and media.",
    details: [
      "Founded and lead Old Alex Hub LLC as a portfolio company connecting technology investing, operating business ownership, and hands-on product execution.",
      "Build and guide TaxiOps, an operations platform for taxi and NEMT providers covering onboarding, dispatch, fleet visibility, mobile trip execution, fares, receipts, reporting, and operations intelligence.",
      "Develop and publish practical digital products across education, productivity, family organization, transportation, finance, EV ownership, offline-first utilities, and mobile entertainment.",
      "Operate After Hours Audio, a faceless ambient media venture focused on late-night instrumental music, focus, and calm.",
      "Manage portfolio strategy across Trans Voyage Taxi, TaxiOps, mobile products, content/media, AI-assisted systems, and long-term venture growth.",
    ],
  },
  {
    title: "Manager of Mobility Services Data - LYNX",
    period: "10/2021 - 05/2026",
    start: 2021,
    summary:
      "Led cross-functional mobility data initiatives for 56,000+ monthly trips, audit automation, and executive reporting.",
    details: [
      "Managed the data lifecycle of 56,000+ monthly trips with reconciliation accuracy and anomaly detection.",
      "Reduced altered On-Time Performance incidents to near-zero by tightening controls and monitoring.",
      "Introduced R-driven automation for audits, statistical reports, and operational analytics.",
      "Owned internal audits (ADA, FTA, agency) and executive-ready dashboards for stakeholders.",
    ],
  },
  {
    title: "Co-Owner, Board Member & Chief Technology and Data Officer - Trans Voyage Taxi",
    period: "10/2021 - Present",
    start: 2021,
    summary:
      "Guide ownership, governance, technology, and data strategy for a Denver-based taxi operation.",
    details: [
      "Oversee strategic growth and financial direction as co-owner and board member.",
      "Lead technology and data strategy, aligning dispatch, trip tracking, reporting, and compliance systems with company goals.",
      "Architected a custom SaaS platform for dispatch, trip tracking, invoicing, driver oversight, and compliance workflows.",
      "Continue supporting operational visibility, governance, and long-term business development.",
    ],
  },
  {
    title: "Co-Owner, Board Member, Executive Director of Operations - Trans Voyage Taxi",
    period: "02/2017 - 10/2021",
    start: 2017,
    summary:
      "Founded and scaled taxi operations from launch through growth, contracts, compliance, and driver network development.",
    details: [
      "Scaled operations from launch to 75 active drivers completing 450+ daily trips and approximately 165,000 annual trips.",
      "Directed recruiting, finance, insurance, risk, compliance, technology, and driver relations during the growth phase.",
      "Negotiated airport and corporate contracts while strengthening revenue streams and market position.",
      "Led regulatory readiness and compliance operations that supported zero-finding Public Utilities Commission audits.",
    ],
  },
  {
    title: "Talent Acquisition Manager - Metro Transportation",
    period: "04/2017 - 05/2018",
    start: 2017,
    summary: "Reengineered hiring for 500+ drivers with forecasting dashboards and electronic records.",
    details: [
      "Created a complete Hiring Packet and digitized records with proactive expiration tracking.",
      "Led recruiters to streamline onboarding for 500+ active drivers with forecasting dashboards.",
      "Identified and remediated over $1M in potential compliance violations during system transition.",
    ],
  },
  {
    title: "General Manager - Green Taxi Cooperative",
    period: "03/2016 - 04/2017",
    start: 2016,
    summary: "Founding leadership scaling operations to 800+ drivers and building operational systems.",
    details: [
      "Launched company infrastructure, hiring, and tech platforms to scale to 800+ drivers in year one.",
      "Managed a large workforce and established compliant, scalable processes and systems.",
    ],
  },
  {
    title: "General Manager - Mile High Cab, Inc",
    period: "02/2014 - 03/2016",
    start: 2014,
    summary: "Led restructuring, launched first mobile booking app, and improved operational standards.",
    details: [
      "Turned around operations, enforced new policies, and launched the mobile taxi booking app.",
      "Managed 200+ employees and improved dispatch efficiency and customer satisfaction.",
    ],
  },
  {
    title: "General Store Manager - Ralston Convenience Store",
    period: "08/2012 - 07/2014",
    start: 2012,
    summary: "Operated store-level management with focus on operations, sales, and team development.",
    details: [
      "Directed sales strategies, scheduling, supplier management, and team development.",
      "Implemented inventory/security measures and led new store development.",
    ],
  },
];

const education = [
  {
    title: "Master of Science in Data Science",
    org: "Eastern University, PA",
    years: "2024-2025",
    status: "Completed",
  },
  {
    title: "Bachelor of Arts in Political Science",
    org: "Metropolitan State University of Denver",
    years: "2014-2020",
    status: "Completed",
  },
];

const awards = [
  "Certificate of Appreciation - Denver International Airport (2019)",
  "David Langston Excellence Award - Central Florida Community College (2009)",
];

const languages = ["Arabic - Fluent", "English - Fluent"];

const Experiences = () => {
  const [projectStats, setProjectStats] = useState({ total: 0, ai: 0, ops: 0 });
  const [openIdx, setOpenIdx] = useState(null);
  const [copied, setCopied] = useState(false);

  const computeProjectStats = (list) => {
    const aiKeywords = ["ai", "ml", "machine learning", "forecast", "nlp", "automation", "predict", "chatbot"];
    const opsKeywords = ["ops", "operations", "platform", "dispatch", "lifecycle", "management", "reconciliation"];
    const norm = (txt = "") => txt.toLowerCase();
    const ai = list.filter(
      (p) =>
        aiKeywords.some((k) => norm(p.title || "").includes(k)) ||
        aiKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    const ops = list.filter(
      (p) =>
        opsKeywords.some((k) => norm(p.title || "").includes(k)) ||
        opsKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    return { total: list.length, ai, ops };
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const resp = await axios.get(API_ENDPOINTS.getArticles);
        if (resp.status === 200 && resp.data?.articles) {
          setProjectStats(computeProjectStats(resp.data.articles));
        }
      } catch (err) {
        // ignore errors
      }
    };
    fetchProjects();
  }, []);

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText("+13525931947");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setCopied(false);
      alert("Call: +1 (352) 593-1947");
    }
  };

  return (
    <div className="experiences-page">
      <section className="container py-5">
        <div className="experience-hero">
          <div>
            <p className="eyebrow">Experience</p>
            <h1 className="display-6 fw-bold">Operator, data leader, founder</h1>
            <p className="lead">
              More than a decade across transportation network operations, mobility data, BI, compliance, and
              founder-led execution. I build systems that scale, stay compliant, and keep teams accountable.
            </p>
            <div className="experience-tags">
              <span className="tag">Operational analytics</span>
              <span className="tag">AI for efficiency</span>
              <span className="tag">Data governance</span>
            </div>
            <div className="hero-actions">
              <a className="btn btn-primary btn-sm" href="mailto:mohamed@mohamedgad.com">
                Email
              </a>
              <button className="btn btn-outline-primary btn-sm" type="button" onClick={copyPhone}>
                <Phone size={14} className="me-1" />
                {copied ? "Copied!" : "Copy number"}
              </button>
              <a
                className="btn btn-outline-secondary btn-sm"
                href="https://www.mohamedgad.com"
                target="_blank"
                rel="noreferrer"
              >
                Website
              </a>
              <a
                className="btn btn-dark btn-sm"
                href="https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn
              </a>
              <a
                className="btn btn-outline-dark btn-sm"
                href="https://github.com/OldAlexhub"
                target="_blank"
                rel="noreferrer"
              >
                <Github size={14} className="me-1" />
                GitHub
              </a>
            </div>
          </div>
          <div className="hero-metrics">
            <div className="metric-card">
              <div className="metric-value">56k+</div>
              <div className="metric-label">Monthly trips governed</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">2000+</div>
              <div className="metric-label">Drivers grown</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{projectStats.ai || "-"}</div>
              <div className="metric-label">AI/ML automations</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{projectStats.ops || "-"}</div>
              <div className="metric-label">Ops platforms and systems</div>
            </div>
          </div>
        </div>

        <section className="my-5">
          <div className="section-heading d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <p className="eyebrow mb-1">Work</p>
              <h2 className="fw-bold mb-0">Career highlights</h2>
            </div>
          </div>
          <div className="row">
            {expData.map((exp, idx) => (
              <div key={idx} className="col-md-6 mb-4">
                <div className="experience-card h-100">
                  <div className="d-flex align-items-start mb-2">
                    <div className="icon-circle">
                      <Briefcase size={16} />
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1">{exp.title}</h5>
                      <p className="text-muted small mb-0">({exp.period})</p>
                    </div>
                    <div className="ms-2 align-self-start">
                      <span className="timeline-badge">{exp.start}</span>
                    </div>
                  </div>
                  <p className="mb-2">{exp.summary}</p>

                  <div className={`collapse-body ${openIdx === idx ? "open" : ""}`}>
                    <ul>
                      {exp.details.map((d, di) => (
                        <li key={di}>{d}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-2">
                    <button
                      className="read-toggle"
                      onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                      aria-expanded={openIdx === idx}
                    >
                      <span>{openIdx === idx ? "Read less" : "Read more"}</span>
                      <ChevronDown className={`chev ${openIdx === idx ? "open" : ""}`} size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="row g-4 my-5">
          <div className="col-md-6">
            <div className="info-card h-100">
              <div className="d-flex align-items-center mb-2">
                <Award size={18} className="me-2 text-primary" />
                <div>
                  <p className="eyebrow mb-0">Education</p>
                  <h5 className="mb-0">Academic foundation</h5>
                </div>
              </div>
              <ul className="list-unstyled mb-0">
                {education.map((edu) => (
                  <li key={edu.title} className="mb-3 d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold">{edu.title}</div>
                      <div className="text-muted small">{edu.org}</div>
                    </div>
                    <div className="text-end">
                      <div className="timeline-badge">{edu.years}</div>
                      <div className="text-success small">{edu.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-md-6">
            <div className="info-card h-100">
              <div className="d-flex align-items-center mb-2">
                <Award size={18} className="me-2 text-primary" />
                <div>
                  <p className="eyebrow mb-0">Awards</p>
                  <h5 className="mb-0">Recognition</h5>
                </div>
              </div>
              <ul className="list-unstyled mb-3">
                {awards.map((a) => (
                  <li key={a} className="mb-2">
                    {a}
                  </li>
                ))}
              </ul>

              <div className="d-flex align-items-center mb-2">
                <Globe size={18} className="me-2 text-primary" />
                <div>
                  <p className="eyebrow mb-0">Languages</p>
                  <h5 className="mb-0">Communication</h5>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {languages.map((l) => (
                  <span key={l} className="chip">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="text-center my-5">
          <h3>Projects</h3>
          <p className="text-muted mb-3">Case studies on mobility platforms, ethical AI, and forecasting.</p>
          <Link to="/projects" className="btn btn-outline-primary btn-lg">
            View all projects <ArrowRight size={18} className="ms-1" />
          </Link>
        </section>
      </section>
    </div>
  );
};

export default Experiences;
